import { useState } from "react";
import type {
  CreateStoreResponse,
  StoreKind,
  SubscriptionPlanView,
} from "@/lib/api/types";
import { Alert, Banner, Button, Checkbox, Field, Input, SearchSelect } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { isValidStoreSlug } from "@/lib/routing/store-slug";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";
import { useCreateSellerStoreMutation } from "../services/useCreateSellerStoreMutation";
import { describeCreateError } from "./createErrors";
import { StoreSlugField } from "./StoreSlugField";

function optionalString(formData: FormData, name: string) {
  const rawValue = formData.get(name);
  if (typeof rawValue !== "string" || rawValue.trim() === "") return undefined;
  return rawValue;
}

function optionalNumber(formData: FormData, name: string) {
  const rawValue = optionalString(formData, name);
  if (rawValue === undefined) return undefined;
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * A business (online store or restaurant) for an EXISTING seller. Fields only:
 * the submit button sits in the drawer bar and targets `formId`.
 */
export function StoreCreateForm({
  formId,
  sellerId,
  locale,
  plans,
  storeKind,
  onSuccess,
  onSubmittingChange,
}: {
  formId: string;
  sellerId: string;
  locale: Locale;
  plans: SubscriptionPlanView[];
  storeKind: StoreKind;
  onSuccess?: (result: CreateStoreResponse) => void;
  onSubmittingChange?: (submitting: boolean) => void;
}) {
  const t = useTranslations();
  const isDefault = storeKind === "DEFAULT";
  const legacyPlans = plans.filter(
    (plan) => plan.type !== "FREE" && plan.type !== "PREMIUM",
  );
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriptionPlanId, setSubscriptionPlanId] = useState("");
  const [storeSlug, setStoreSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [isPremium, setIsPremium] = useState(
    !isDefault && legacyPlans.length === 0,
  );
  const { mutateAsync: createStore } = useCreateSellerStoreMutation();

  const setSubmitting = (value: boolean) => {
    setIsSubmitting(value);
    onSubmittingChange?.(value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    setError(null);
    setFieldErrors({});
    const formData = new FormData(event.currentTarget);
    const requiredErrors = getRequiredFieldErrors(
      formData,
      ["storeName"],
      t("admin.storeNameRequired"),
    );
    if (!isDefault && !isPremium && !subscriptionPlanId) {
      requiredErrors.subscriptionPlanId = t("admin.planRequired");
    }
    setSlugTouched(true);
    if (isDefault && storeSlug !== "" && !isValidStoreSlug(storeSlug)) {
      requiredErrors.storeSlug = t(keys.admin.storeSlugInvalid);
    }

    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      return;
    }

    setSubmitting(true);
    try {
      const result = await createStore({
        sellerId,
        storeName: String(formData.get("storeName") ?? ""),
        storeSlug: isDefault && storeSlug ? storeSlug : undefined,
        storeKind,
        maxProductImageCount: optionalNumber(formData, "maxProductImageCount"),
        isPremium: isDefault ? false : isPremium,
        subscriptionPlanId:
          isDefault || isPremium ? undefined : subscriptionPlanId,
        subscriptionStartDate: optionalString(formData, "subscriptionStartDate"),
        subscriptionOverrideAmount: optionalNumber(
          formData,
          "subscriptionOverrideAmount",
        ),
        subscriptionOverridePeriodDays: optionalNumber(
          formData,
          "subscriptionOverridePeriodDays",
        ),
      });
      setSubmitting(false);
      onSuccess?.(result);
    } catch (caught) {
      const { field, message } = describeCreateError(caught, t);
      if (field === "storeSlug") setFieldErrors({ storeSlug: message });
      else setError(message);
      setSubmitting(false);
    }
  };

  return (
    <form
      id={formId}
      onSubmit={handleSubmit}
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
    >
      {error ? (
        <Banner
          tone="error"
          title={t("admin.businessCreateFailedTitle")}
          actions={
            <Button size="sm" type="submit" loading={isSubmitting} loadingLabel={t(keys.shell.retrying)}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {error} {t("admin.nothingCreated")}
        </Banner>
      ) : null}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 180px)",
          gap: 12,
          alignItems: "start",
        }}
      >
        <Field
          label={t(
            storeKind === "RESTAURANT" ? keys.admin.restaurantName : keys.common.storeName,
          )}
          htmlFor="storeName"
          error={fieldErrors.storeName}
        >
          <Input
            id="storeName"
            name="storeName"
            placeholder={t(keys.placeholders.storeName)}
            invalid={Boolean(fieldErrors.storeName)}
          />
        </Field>
        <Field
          label={t(keys.admin.maxProductImageCountLabel)}
          htmlFor="maxProductImageCount"
          help={t(keys.admin.maxProductImageCountHelper)}
        >
          <Input
            id="maxProductImageCount"
            name="maxProductImageCount"
            type="number"
            inputMode="numeric"
            dir="ltr"
            placeholder="3"
          />
        </Field>
      </div>
      {isDefault ? (
        <>
          <StoreSlugField
            value={storeSlug}
            onChange={(next) => {
              setStoreSlug(next);
              setFieldErrors((previous) => {
                const rest = { ...previous };
                delete rest.storeSlug;
                return rest;
              });
            }}
            error={fieldErrors.storeSlug}
            showInvalid={slugTouched}
            onBlur={() => setSlugTouched(true)}
          />
          <Alert tone="info">{t(keys.admin.tierCreateFree)}</Alert>
        </>
      ) : (
        <Checkbox
          label={t(keys.admin.storeCreatePremiumLabel)}
          description={t(keys.admin.storeCreatePremiumHelper)}
          checked={isPremium}
          disabled={legacyPlans.length === 0}
          onChange={setIsPremium}
        />
      )}
      {isDefault || isPremium ? null : (
        <>
          <Field
            label={t(keys.admin.subscriptionSelectLabel)}
            htmlFor="subscriptionPlanId"
            error={fieldErrors.subscriptionPlanId}
          >
            <div style={{ maxWidth: 360 }}>
              <SearchSelect
                id="subscriptionPlanId"
                invalid={Boolean(fieldErrors.subscriptionPlanId)}
                label={t(keys.admin.subscriptionSelectLabel)}
                value={subscriptionPlanId}
                onChange={setSubscriptionPlanId}
                placeholder={t(keys.admin.subscriptionSelectPlaceholder)}
                emptyLabel={t(keys.common.noResults)}
                options={legacyPlans.map((plan) => ({
                  value: plan.id,
                  label: pickLocalized(locale, plan.nameAr, plan.nameEn),
                }))}
              />
            </div>
          </Field>
          <div className="rd-fieldset">
            <div className="rd-fields-inline">
              <Field
                label={t(keys.admin.subscriptionStartDateLabel)}
                htmlFor="subscriptionStartDate"
                help={t(keys.admin.subscriptionStartDateHelper)}
              >
                <Input
                  id="subscriptionStartDate"
                  name="subscriptionStartDate"
                  type="date"
                  style={{ width: 170 }}
                />
              </Field>
              <Field
                label={t(keys.admin.subscriptionOverrideAmountLabel)}
                htmlFor="subscriptionOverrideAmount"
                help={t(keys.admin.subscriptionOverrideAmountHelper)}
              >
                <Input
                  id="subscriptionOverrideAmount"
                  name="subscriptionOverrideAmount"
                  type="number"
                  step="0.01"
                  dir="ltr"
                  placeholder="0"
                  style={{ width: 170 }}
                />
              </Field>
              <Field
                label={t(keys.admin.subscriptionOverridePeriodLabel)}
                htmlFor="subscriptionOverridePeriodDays"
                help={t(keys.admin.subscriptionOverridePeriodHelper)}
              >
                <Input
                  id="subscriptionOverridePeriodDays"
                  name="subscriptionOverridePeriodDays"
                  type="number"
                  dir="ltr"
                  placeholder="30"
                  style={{ width: 170 }}
                />
              </Field>
            </div>
          </div>
        </>
      )}
    </form>
  );
}
